// Location name translator service using AWS Translate
// Translates city names for the location display

import { BaseTranslationService, type TranslationCacheConfig } from './baseTranslationService'

export class LocationNameTranslator extends BaseTranslationService {
  constructor(targetLanguage: string = 'en') {
    const config: TranslationCacheConfig = {
          localStoragePrefix: 'wital_location_names_',
    cacheKeyPrefix: 'location_names_',
      serviceName: 'Location Translator'
    }
    super(config, targetLanguage)
  }

  // Translate a single city name
  async translateLocationName(locationName: string): Promise<string> {
    // If target language is English, no translation needed
    if (!this.isTranslationNeeded() || !locationName || locationName.trim() === '') {
      return locationName
    }

    console.log(`🔍 ${this.config.serviceName}: Translating "${locationName}" to ${this.targetLanguage}`)

    try {
      // Get cached translations or fetch new ones
      const translations = await this.getTranslations({ [locationName]: locationName })
      return translations[locationName] || locationName

    } catch (error) {
      console.warn(`⚠️ ${this.config.serviceName}: Translation failed for "${locationName}":`, error)
      return locationName // Fallback to original name
    }
  }

  // Translate multiple location names
  async translateLocationNames(locationNames: string[]): Promise<Record<string, string>> {
    // If target language is English, no translation needed
    if (!this.isTranslationNeeded()) {
      return locationNames.reduce((acc, name) => {
        acc[name] = name
        return acc
      }, {} as Record<string, string>)
    }

    console.log(`🔍 ${this.config.serviceName}: Translating ${locationNames.length} location names to ${this.targetLanguage}`)

    try {
      // Create map of location names to translate
      const locationNameMap = locationNames.reduce((acc, name) => {
        acc[name] = name
        return acc
      }, {} as Record<string, string>)

      // Get cached translations or fetch new ones
      return await this.getTranslations(locationNameMap)

    } catch (error) {
      console.warn(`⚠️ ${this.config.serviceName}: Translation failed for location names:`, error)
      
      // Fallback to original names
      return locationNames.reduce((acc, name) => {
        acc[name] = name
        return acc
      }, {} as Record<string, string>)
    }
  }
}

// Create and export default instance
export const locationNameTranslator = new LocationNameTranslator()

// Helper function for easier usage
export const translateLocationName = async (
  locationName: string, 
  targetLanguage: string
): Promise<string> => {
  const translator = new LocationNameTranslator(targetLanguage)
  return await translator.translateLocationName(locationName)
}

// Helper function for translating multiple location names
export const translateLocationNames = async (
  locationNames: string[], 
  targetLanguage: string
): Promise<Record<string, string>> => {
  const translator = new LocationNameTranslator(targetLanguage)
  return await translator.translateLocationNames(locationNames)
} 